import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, CreditCard, ExternalLink, Loader2, Crown, Trash2, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { planIdFromProduct } from "@/data/stripe-plans";
import { plans as dataplans } from "@/data/plans";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function PortalSettings() {
  const { user, subscription } = useAuth();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("first_name, last_name, company")
      .eq("id", user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          setFirstName(data.first_name ?? "");
          setLastName(data.last_name ?? "");
          setCompany(data.company ?? "");
        }
        setLoading(false);
      });
  }, [user]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    await supabase
      .from("profiles")
      .update({ first_name: firstName, last_name: lastName, company })
      .eq("id", user.id);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const openCustomerPortal = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch {
      toast.error("Kundenportal konnte nicht geöffnet werden.");
    } finally {
      setPortalLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== "DELETE") return;
    setDeleteLoading(true);
    try {
      const { error } = await supabase.functions.invoke("delete-account");
      if (error) throw error;
      toast.success("Ihr Account wurde gelöscht.");
      navigate("/");
    } catch {
      toast.error("Fehler beim Löschen des Accounts.");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Derive plan info
  const planKey = subscription.productId ? planIdFromProduct(subscription.productId) : null;
  const plan = planKey ? dataplans.find((p) => p.id === planKey) : null;
  const isTrialing = subscription.status === "trialing";
  const isActive = subscription.subscribed;
  const endDate = subscription.subscriptionEnd
    ? new Date(subscription.subscriptionEnd).toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : null;

  if (loading) {
    return (
      <div className="container py-8 max-w-2xl flex justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="container py-8 max-w-2xl space-y-8">
      <div>
        <h1 className="font-display text-2xl font-bold text-foreground mb-1">Konto</h1>
        <p className="text-muted-foreground">Profil, Abonnement und Zahlungsdaten verwalten.</p>
      </div>

      {/* ── Profile ─────────────────────────────────────────── */}
      <form onSubmit={handleSave} className="rounded-2xl border border-border bg-card p-6 shadow-card space-y-5">
        <h2 className="font-display font-bold text-lg text-foreground">Profildaten</h2>
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="firstName">Vorname</Label>
            <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Nachname</Label>
            <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="company">Firma</Label>
          <Input id="company" value={company} onChange={(e) => setCompany(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="settingsEmail">E-Mail-Adresse</Label>
          <Input id="settingsEmail" type="email" value={user?.email ?? ""} disabled className="bg-muted" />
        </div>
        <div className="flex items-center gap-3 pt-2">
          <Button type="submit" className="bg-primary text-primary-foreground">Speichern</Button>
          {saved && (
            <span className="flex items-center gap-1 text-sm text-primary">
              <Check className="h-4 w-4" /> Gespeichert
            </span>
          )}
        </div>
      </form>

      {/* ── Subscription ────────────────────────────────────── */}
      <div className="rounded-2xl border border-border bg-card p-6 shadow-card space-y-5">
        <h2 className="font-display font-bold text-lg text-foreground">Abonnement</h2>

        {isActive ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <Crown className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-foreground">{plan?.name ?? "Plan"}</p>
                <p className="text-sm text-muted-foreground">
                  {isTrialing ? "Testphase" : "Aktiv"}
                  {endDate && ` · ${isTrialing ? "endet" : "nächste Abrechnung"} am ${endDate}`}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button variant="outline" size="sm" onClick={openCustomerPortal} disabled={portalLoading}>
                {portalLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ExternalLink className="mr-2 h-4 w-4" />}
                Abo verwalten
              </Button>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/preise">Plan upgraden</Link>
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Sie haben aktuell kein aktives Abonnement.</p>
            <Button size="sm" asChild className="bg-primary text-primary-foreground">
              <Link to="/preise">Plan auswählen</Link>
            </Button>
          </div>
        )}
      </div>

      {/* ── Payment ─────────────────────────────────────────── */}
      <div className="rounded-2xl border border-border bg-card p-6 shadow-card space-y-4">
        <h2 className="font-display font-bold text-lg text-foreground">Zahlungsdaten</h2>
        <p className="text-sm text-muted-foreground">
          Ihre Zahlungsdaten werden sicher über unseren Zahlungsdienstleister verwaltet.
        </p>
        {isActive ? (
          <Button variant="outline" size="sm" onClick={openCustomerPortal} disabled={portalLoading}>
            {portalLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
            Zahlungsmethode verwalten
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground">
            Zahlungsdaten werden beim Abschluss eines Plans hinterlegt.
          </p>
        )}
      </div>

      {/* ── Delete Account ──────────────────────────────────── */}
      <div className="rounded-2xl border border-destructive/30 bg-card p-6 shadow-card space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-destructive/10">
            <Trash2 className="h-5 w-5 text-destructive" />
          </div>
          <div>
            <h2 className="font-display font-bold text-lg text-foreground">Account löschen</h2>
            <p className="text-sm text-muted-foreground">Diese Aktion kann nicht rückgängig gemacht werden.</p>
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          Wenn Sie Ihren Account löschen, werden alle Ihre persönlichen Daten unwiderruflich entfernt. Aktive Abonnements
          werden automatisch gekündigt. Bestelldaten werden gemäß gesetzlicher Aufbewahrungsfristen anonymisiert.
        </p>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" size="sm">
              <Trash2 className="mr-2 h-4 w-4" />
              Account unwiderruflich löschen
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Account endgültig löschen?
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-3">
                <span className="block">
                  Alle Ihre Daten werden unwiderruflich gelöscht. Aktive Abonnements werden gekündigt.
                </span>
                <span className="block font-semibold text-foreground">
                  Geben Sie <span className="font-mono bg-muted px-1.5 py-0.5 rounded text-destructive">DELETE</span> ein, um zu bestätigen:
                </span>
                <Input
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  placeholder="DELETE"
                  className="font-mono"
                />
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeleteConfirm("")}>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAccount}
                disabled={deleteConfirm !== "DELETE" || deleteLoading}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Endgültig löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
